import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { empty, of } from 'rxjs';
import { map, catchError, takeWhile } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import * as _lodash from 'lodash';
import { Profile } from '../../../../profiles/models/profile';
import { existingProfilenameValidator } from '../../../../common/validators/profilename-available.validator';
import { ProfileHttpService } from '../../../../profiles/services/profile-http.service';
import { WidgetConfiguration } from '../../../../widget-configs/models/widget-configuration';
import * as ProfileTypes from '../../../../profiles/models/profile-types';
import { RouteContextService } from '../../../../context/services/route-context.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'mojo-profile-editor',
  templateUrl: './profile-editor.component.html'
})
export class ProfileEditorComponent implements OnInit, OnDestroy {

  get name(): AbstractControl {
    return this.form ? this.form.controls['name'] : null;
  }

  constructor(private formBuilder: UntypedFormBuilder,
    private profileHttpService: ProfileHttpService,
    private messageService: ToastrService,
    private routeContextService: RouteContextService,
    private activatedRoute: ActivatedRoute
    ) {
    this.buildForm();
  }

  isAlive: boolean = true;
  @Input() dialogTitle = 'Profile Details';
  @Input() widgetConfigurations: WidgetConfiguration[] = [];

  @Output() onOpen = new EventEmitter();
  @Output() onCancel = new EventEmitter();
  @Output() onSaveComplete = new EventEmitter<any>();
  @Output() onDeleted = new EventEmitter<any>();

  private profile: Profile;
  private original: Profile;

  public isNewProfile: boolean;
  public selectedWidgetConfigs: WidgetConfiguration[] = [];

  form: UntypedFormGroup;
  displayDialog: boolean;
  allowActions = true;
  private cloneProfile = (profile: Profile = null) => _lodash.cloneDeep(profile || this.profile);

  save(): void {
    const updatedProfile = this.mapFormToProfile();
    updatedProfile.selectedWidgetConfigurationIds = this.selectedWidgetConfigs.map(({ id }) => id);

    this.allowActions = false;
    if (this.isNewProfile) {
      this.profileHttpService.createProfile(updatedProfile).pipe(
        map(() => {
          this.displayDialog = false;
          this.allowActions = true;
          this.messageService.success(`New Profile '${updatedProfile.name}' created`, 'New Profile Created');
          this.onSaveComplete.emit(null);
        }),
        catchError(() => {
          this.messageService.error(`Profile '${updatedProfile.name}' not saved`, 'Save Error');
          this.allowActions = true;
          return empty();
        }),
        takeWhile(() => this.isAlive)
      ).subscribe();
    } else {
      this.profileHttpService.updateProfile(updatedProfile).pipe(
        catchError(() => {
          this.messageService.error(`Profile '${updatedProfile.name}' not saved`, 'Save Error');
          this.allowActions = true;
          return of([]);
        }),
        takeWhile(() => this.isAlive)
      ).subscribe(() => {
        this.displayDialog = false;
        this.allowActions = true;
        this.messageService.success(`Profile '${updatedProfile.name}' Updated`, 'Profile Updated');
        this.onSaveComplete.emit(null);
      });
    }
  }

  createCopy() {
    const updatedProfile = this.mapFormToProfile();
    updatedProfile.selectedWidgetConfigurationIds = this.selectedWidgetConfigs.map(({ id }) => id);

    this.profileHttpService.createcopy(updatedProfile).pipe(
      map(() => {
        this.displayDialog = false;
        this.allowActions = true;
        this.messageService.success(`Profile '${updatedProfile.name}' created`, 'Profile Copied');
        this.onSaveComplete.emit(null);
      }),
      catchError(() => {
        this.messageService.error(`Profile '${updatedProfile.name}' not copied`, 'Save Error');
        this.allowActions = true;
        return empty();
      }),
      takeWhile(() => this.isAlive)
    ).subscribe();
  }

  displayCreateCopyButton() {
    return !this.isNewProfile;
  }

  displayDeleteButton() {
    if (this.profile && !this.isNewProfile) {
      return this.profile.type !== ProfileTypes.Standard;
    }
    return false;
  }

  displaySaveButton() {
    if (this.profile) {
      return this.profile.type !== ProfileTypes.Standard;
    }
    return false;
  }

  cancel(): void {
    this.displayDialog = false;
    this.onCancel.emit();
  }

  deleteProfile(): void {
    this.profileHttpService.deleteProfile(this.profile.id).pipe(
      map(() => {
        this.displayDialog = false;
        this.allowActions = true;
        this.messageService.success(`Profile '${this.profile.name}' deleted`, 'Profile Deleted');
        this.onDeleted.emit(null);
      }),
      catchError((err: HttpErrorResponse) => {
        console.log(err);
        this.messageService.error(`Could not delete Profile '${this.profile.name}'`, 'Delete Error');
        this.allowActions = true;
        return empty();
      }),
      takeWhile(() => this.isAlive)
    ).subscribe();
  }

  private mapProfileToForm(profile: Profile) {
    return {
      name: profile.name
    };
  }

  private mapFormToProfile(): Profile {
    const profile = this.cloneProfile(this.profile);
    profile.name = this.name.value;
    profile.product = this.routeContextService.getProductFromRouteData(this.activatedRoute);
    return profile;
  }

  showDialog(profile: Profile, createNew: boolean): void {
    this.selectedWidgetConfigs = [];
    this.isNewProfile = createNew;
    this.displayDialog = true;
    this.onOpen.emit();
    this.original = this.cloneProfile(profile);
    this.profile = this.cloneProfile(this.original);

    this.buildForm();
    this.form.setValue(this.mapProfileToForm(profile));
    this.selectedWidgetConfigs = this.widgetConfigurations.filter(config => profile.selectedWidgetConfigurationIds.includes(config.id));
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.isAlive = false;
  }

  private buildForm(): void {
    const nameMaxlength = 254;

    this.form = this.formBuilder.group(
      {
        name: [
          '',
          [
            Validators.required,
            Validators.maxLength(nameMaxlength)
          ],
          [
            ... this.isNewProfile ? [existingProfilenameValidator(this.profileHttpService, this.routeContextService.getProductFromRouteData(this.activatedRoute))] : []
          ]
        ]
      }
    );
  }
}
